import Vue from 'vue';
import App from './App.vue';
import VueMoment from 'vue-moment';
import LoadScript from 'vue-plugin-load-script';
import moment from 'moment-timezone';
// Bring in required CSS globally to avoid PostCSS SFC issues
import 'video.js/dist/video-js.css';
import 'videojs-ima/dist/videojs.ima.css';

// Import version service
import { versionService } from './services/version.service';

Vue.use(VueMoment, { moment });
Vue.use(LoadScript);

Vue.config.productionTip = false;

// Log player version on startup
versionService.logVersion();

// Global error handler to catch the getElementsByClassName error
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('getElementsByClassName')) {
    console.error('[GLOBAL ERROR] getElementsByClassName error caught:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      stack: event.error?.stack
    });
  }
});

// Custom Light DOM Web Component
class EtvPlayerElement extends HTMLElement {
  private vueInstance: Vue | null = null;

  constructor() {
    super();
    // NO Shadow DOM creation - everything in Light DOM
  }

  connectedCallback() {
    // Create Vue instance directly in Light DOM
    this.createVueInstance();
  }

  disconnectedCallback() {
    if (this.vueInstance) {
      this.vueInstance.$destroy();
      this.vueInstance = null;
    }
  }

  private createVueInstance() {
    // Get all attributes as props
    const props: any = {};

    for (const attr of this.attributes) {
      const propName = this.attributeToCamelCase(attr.name);
      props[propName] = attr.value;
    }

    // Convert boolean-like attributes
    if (props.autoplay === '' || props.autoplay === 'autoplay' || props.autoplay === 'true') {
      props.autoplay = true;
    } else if (props.autoplay === 'false') {
      props.autoplay = false;
    }

    console.log('[ETV-PLAYER] Creating Vue instance with props:', props);

    // Create Vue instance and mount it into a child of this element (Light DOM)
    // so the custom element itself remains in the DOM and is discoverable.
    this.vueInstance = new Vue({
      render: (h) => h(App, { props }),
    });

    // Clear previous content (if any) and mount into a fresh container
    while (this.firstChild) this.removeChild(this.firstChild);
    this.vueInstance.$mount();
    this.appendChild(this.vueInstance.$el);
  }

  private attributeToCamelCase(attr: string): string {
    return attr.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  }

  // Watch for attribute changes
  static get observedAttributes() {
    return ['token', 'autoplay', 'ad-target-tag', 'width', 'ratio', 'id', 'loglevel', 'query', 'sortfield', 'sortorder', 'limit', 'offset', 'maxduration'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (this.vueInstance && oldValue !== newValue) {
      console.log(`[ETV-PLAYER] Attribute ${name} changed from ${oldValue} to ${newValue}`);
      // Recreate the Vue instance with new props
      this.disconnectedCallback();
      this.createVueInstance();
    }
  }
}

// Define the custom element (for Light DOM builds)
window.customElements.define('etv-player', EtvPlayerElement);

// For iframe build: Mount Vue app directly to #app div
// This runs when index.html is loaded inside an iframe with URL parameters
if (document.getElementById('app')) {
  // Parse URL parameters for iframe mode
  const urlParams = new URLSearchParams(window.location.search);
  const props: any = {};

  // Map common URL parameters to props
  if (urlParams.has('token') || urlParams.has('playerToken')) {
    props.token = urlParams.get('token') || urlParams.get('playerToken');
  }
  if (urlParams.has('channel')) {
    props.channel = urlParams.get('channel');
  }
  if (urlParams.has('autoplay')) {
    props.autoplay = urlParams.get('autoplay') === 'true';
  }
  if (urlParams.has('width')) {
    props.width = urlParams.get('width');
  }
  if (urlParams.has('ratio')) {
    props.ratio = urlParams.get('ratio');
  }
  if (urlParams.has('loglevel')) {
    props.loglevel = urlParams.get('loglevel');
  }
  if (urlParams.has('adTargetTag')) {
    props.adTargetTag = urlParams.get('adTargetTag');
  }

  // Playlist query parameters (all lowercase, no dashes for consistency)
  if (urlParams.has('query')) {
    props.query = urlParams.get('query');
  }
  if (urlParams.has('sortfield')) {
    props.sortfield = urlParams.get('sortfield');
  }
  if (urlParams.has('sortorder')) {
    props.sortorder = urlParams.get('sortorder');
  }
  if (urlParams.has('limit')) {
    props.limit = urlParams.get('limit');
  }
  if (urlParams.has('offset')) {
    props.offset = urlParams.get('offset');
  }
  if (urlParams.has('maxduration')) {
    props.maxduration = urlParams.get('maxduration');
  }

  console.log('[IFRAME MODE] Mounting Vue app with URL params:', props);

  new Vue({
    render: (h) => h(App, { props }),
  }).$mount('#app');
}
